import { Card, CardContent } from "../../../client/components/ui/card";
import { cn } from "../../../client/utils";
import useTranslation from "../../../client/hooks/useTranslation";
import type { CohortRow } from "../../../analytics/adminInsights";

interface Props {
  cohorts: CohortRow[];
}

/** Vert au-dessus de 50 %, ambre au-dessus de 25 %, rouge en dessous. */
function rateTone(rate: number, signups: number): string {
  if (signups === 0) return "text-muted-foreground";
  if (rate >= 50) return "text-emerald-500";
  if (rate >= 25) return "text-amber-500";
  return "text-red-500";
}

/** "2026-09" en libelle localise court, sans dependre d'une table de mois. */
function formatMonth(month: string): string {
  const [year, monthIndex] = month.split("-").map(Number);
  if (!year || !monthIndex) return month;
  // Le 15 evite qu'un decalage de fuseau fasse basculer de mois.
  return new Date(Date.UTC(year, monthIndex - 1, 15)).toLocaleDateString(
    undefined,
    { month: "short", year: "numeric" },
  );
}

const RetentionCohortCard = ({ cohorts }: Props) => {
  const t = useTranslation();
  const hasAnySignup = cohorts.some((cohort) => cohort.signups > 0);

  return (
    <div>
      <h2 className="text-foreground mb-1 text-lg font-semibold">
        {t.adminDashboard.cohortTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.cohortHint}
      </p>

      <Card>
        <CardContent className="pt-4">
          {!hasAnySignup ? (
            <p className="text-muted-foreground py-6 text-center text-xs">
              {t.adminDashboard.cohortEmpty}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[32rem] text-sm">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-left text-xs">
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.cohortMonth}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t.adminDashboard.cohortSignups}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t.adminDashboard.cohortRetained}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t.adminDashboard.cohortRate}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((cohort) => (
                    <tr
                      key={cohort.month}
                      className="border-border/60 border-b last:border-b-0"
                    >
                      <td className="text-foreground py-2.5">
                        {formatMonth(cohort.month)}
                      </td>
                      <td className="text-foreground py-2.5 text-right font-mono">
                        {cohort.signups}
                      </td>
                      <td className="text-foreground py-2.5 text-right font-mono">
                        {cohort.retained}
                      </td>
                      <td className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Barre de proportion : la lecture d'un tableau de
                              cohortes passe surtout par la comparaison visuelle. */}
                          <div className="bg-muted h-1.5 w-16 overflow-hidden rounded-full">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                cohort.retentionRate >= 50
                                  ? "bg-emerald-500"
                                  : cohort.retentionRate >= 25
                                    ? "bg-amber-500"
                                    : "bg-red-500",
                              )}
                              style={{ width: `${cohort.retentionRate}%` }}
                            />
                          </div>
                          <span
                            className={cn(
                              "w-10 font-mono font-semibold",
                              rateTone(cohort.retentionRate, cohort.signups),
                            )}
                          >
                            {cohort.signups === 0
                              ? "-"
                              : `${cohort.retentionRate}%`}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RetentionCohortCard;
